import {useEffect, useState} from 'react';
import {InboxData} from '../types';
import {Flow, FlowFetchResponse} from '../_services/Flow';
import {User} from '../_services/User';

/**
 * Interface defining the return value of useInbox custom hook.
 * @interface UseInboxReturn
 *
 * @property {InboxData} inbox
 * InboxData is an object defining the structure of inbox data.
 *
 * @property {boolean} isLoading
 * A boolean indicating if the inbox data is currently loading.
 *
 * @property {string} inboxLoadingErrorMessage
 * A string that stores the error message if an error occurs during the loading process of the inbox data.
 *
 * @property {Array<object>} availableWorkflows
 * An array of available workflows object.
 *
 * @property {object} filterFormState
 * An object holding the state of the filter form.
 *
 * @property {(filterFormState: object | ((prevState: object) => object)) => void} setFilterFormState
 * A function to set a new filter form state. The formState could be a newFormState value or a function that receives the previous state and returns a newFormState.
 */
interface UseInboxReturn {
    inbox: InboxData;
    isLoading: boolean;
    inboxLoadingErrorMessage: string;
    availableWorkflows: object[];
    filterFormState: object;
    setFilterFormState: (filterFormState: object | ((prevState: object) => object)) => void;
}

/**
 * Custom hook for managing the inbox functionality.
 *
 * @param {string} workflowId - The ID of the workflow for which the inbox is being used
 * @returns {UseInboxReturn} Object containing inbox data, loading status, error message, available workflows, filter form state, and function to set filter form state
 */
export const useInbox = (
    workflowId: string
): UseInboxReturn => {
    const [inbox, setInbox] = useState<InboxData>({} as InboxData);
    const [isLoading, setIsLoading]: any = useState(false);
    const [inboxLoadingErrorMessage, setInboxLoadingErrorMessage]: any = useState('');
    const [availableWorkflows, setAvailableWorkflows] = useState([]);
    const [filterFormState, setFilterFormState] = useState(User.getInstance().getInboxFilter());

    useEffect(() => {
        Flow.getInstance().getInboxForms()
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    setAvailableWorkflows(response.json.map((f: any) => ({
                        label: f.name,
                        value: f.id,
                    })));

                    // if there is <1 available workflows, clear filters
                    // NOTE - this is clearing all filters, this may not be desired behavior when adding more filters
                    if (response?.json?.length < 1) {
                        setFilterFormState({});
                        User.getInstance().setInboxFilter({});
                    }
                } else {
                    setFilterFormState({});
                    User.getInstance().setInboxFilter({});
                }
            })
            .catch((error: any) => {
                console.error(error);
            })
    }, []);

    useEffect(() => {
        User.getInstance().setInboxFilter(filterFormState);
        setIsLoading(true);
        setInboxLoadingErrorMessage('');
        const selectedWorkflowId = filterFormState?.workflow?.value || workflowId;
        Flow.getInstance().getInboxEntries(selectedWorkflowId)
            .then((response: FlowFetchResponse) => {
                if (response.ok && response.json) {
                    setIsLoading(false);
                    setInbox(response.json);

                    // if a workflow is currently selected in the filters but no entries are returned
                    // clear the filter and reload
                    if (filterFormState?.workflow?.value && response?.json?.entries?.length < 1) {
                        setFilterFormState({});
                    }
                } else {
                    throw Error(response?.json?.message);
                }
            })
            .catch((error: any) => {
                setInboxLoadingErrorMessage(error || 'Exception loading Inbox');
            })
            .finally(() => {
                setIsLoading(false);
            })
    }, [filterFormState]);

    return {
        inbox,
        isLoading,
        inboxLoadingErrorMessage,
        availableWorkflows,
        filterFormState,
        setFilterFormState
    };
}